// 6-digit verification code input — ports the auto-advance / paste / backspace
// behavior from auth.js _showVerifyCodeForm into a controlled component.
// Calls onComplete(code) once all six digits are filled.

import { useRef } from 'react'
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react'
import styles from './CodeInput.module.css'

const LENGTH = 6

interface CodeInputProps {
  value: string
  onChange: (code: string) => void
  onComplete?: (code: string) => void
  disabled?: boolean
}

export default function CodeInput({ value, onChange, onComplete, disabled }: CodeInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(LENGTH, ' ').slice(0, LENGTH).split('')

  const setDigit = (index: number, char: string) => {
    const next = digits.map((d) => (d === ' ' ? '' : d))
    next[index] = char
    const joined = next.join('')
    onChange(joined)
    return joined
  }

  const handleInput = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1)
    if (!char) return
    const joined = setDigit(index, char)
    if (index < LENGTH - 1) refs.current[index + 1]?.focus()
    if (joined.replace(/\s/g, '').length === LENGTH) onComplete?.(joined.replace(/\s/g, ''))
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index]?.trim() && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, LENGTH)
    if (!pasted) return
    onChange(pasted)
    refs.current[Math.min(pasted.length, LENGTH) - 1]?.focus()
    if (pasted.length === LENGTH) onComplete?.(pasted)
  }

  return (
    <div className={styles.group} onPaste={handlePaste}>
      {Array.from({ length: LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          className={styles.cell}
          value={digits[i]?.trim() || ''}
          disabled={disabled}
          onChange={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
        />
      ))}
    </div>
  )
}
